/**
 * ── THE ASSET LIBRARY's shared vocabulary (plan `docs/ai/plans/asset-service.md` §4.1–4.3) ──────
 *
 * The three words every other core module and the service agree on: the LANE a request rides
 * (which is the ONLY difference between a boot-time and a mid-play request — §4.3), the STATE a
 * handle is in (§4.2's machine), and the FAILURE CODE a handle carries when it settles without
 * bytes. They live here, in one small file, so `ledger.ts`, `scheduler.ts` and `readiness.ts`
 * never each grow a private copy of the lane list — three copies of `['boot','gameplay',
 * 'prefetch']` is three chances for one to drift, and the drift is silent.
 *
 * PURE (`PURE_DIRS` — `src/assets/core/**`): no three, no DOM, no clock, no randomness. Everything
 * in this directory takes time as an argument and returns warnings as DATA; the service is the one
 * place a warning becomes a `console.warn`, once.
 */

/**
 * The lanes, in PRIORITY ORDER — this array IS the ordering (`lanePriority` indexes into it).
 * `boot` gates `playing`; the other two never do (§4.3).
 */
export const ASSET_LANES = ['boot', 'gameplay', 'prefetch'] as const;
export type AssetLane = (typeof ASSET_LANES)[number];

/** Lower is more urgent: boot 0 · gameplay 1 · prefetch 2. */
export function lanePriority(lane: AssetLane): number {
  return ASSET_LANES.indexOf(lane);
}

/** `true` when `a` is strictly more urgent than `b`. */
export function laneOutranks(a: AssetLane, b: AssetLane): boolean {
  return lanePriority(a) < lanePriority(b);
}

/** Type guard over an untrusted string (a URL param, a JSON field). */
export function isAssetLane(v: unknown): v is AssetLane {
  return typeof v === 'string' && (ASSET_LANES as readonly string[]).includes(v);
}

/**
 * The handle state machine (§4.2): `queued → fetching → parsing → ready | failed | released`.
 * `ready`, `failed` and `released` are SETTLED — a handle in one of them will never move again
 * except `ready → released`. Failure is a state, never an exception (rule 6).
 */
export const HANDLE_STATES = ['queued', 'fetching', 'parsing', 'ready', 'failed', 'released'] as const;
export type HandleState = (typeof HANDLE_STATES)[number];

/** States a handle cannot leave (other than `ready → released`). */
export const SETTLED_HANDLE_STATES: ReadonlySet<HandleState> = new Set(['ready', 'failed', 'released']);

export function isSettledHandleState(s: HandleState): boolean {
  return SETTLED_HANDLE_STATES.has(s);
}

/** Why a handle settled without bytes (§4.2). */
export const FAILURE_CODES = ['unsupported', 'missing', 'network', 'budget', 'aborted'] as const;
export type FailureCode = (typeof FAILURE_CODES)[number];

export interface HandleFailure {
  code: FailureCode;
  message: string;
}
